/**
 * Preference keys for “default tab when you open Studio”.
 * "dashboard" is retired (Dallas, 11 Oct 2026): a stored "dashboard" no longer
 * parses, so it falls back to DMs like an unset preference.
 */
export const STUDIO_DEFAULT_TAB_VALUES = [
  "composer",
  "messages",
  "mail",
  "todo",
] as const;

export type StudioDefaultTab = (typeof STUDIO_DEFAULT_TAB_VALUES)[number];

export const STUDIO_DEFAULT_TAB_LABELS: Record<StudioDefaultTab, string> = {
  todo: "Calendar",
  composer: "Create",
  messages: "DMs",
  mail: "Mail",
};

export const STUDIO_DEFAULT_TAB_STORAGE_KEY = "yatishara-studio-default-tab-v1";

/**
 * Maps preference → workspace tab key. Unset → "" (callers land on DMs).
 * Cold boot with no stored session opens this tab, else DMs — see defaultTabSession.
 */
export function studioTabKeyForDefault(pref: StudioDefaultTab | null | undefined): string {
  switch (pref) {
    case "todo":
      return "todo:home";
    case "messages":
      return "messages:main";
    case "mail":
      return "mail:main";
    case "composer":
      return "messages:main";
    default:
      return "";
  }
}

export function parseStudioDefaultTab(value: unknown): StudioDefaultTab | null {
  if (typeof value !== "string") return null;
  return (STUDIO_DEFAULT_TAB_VALUES as readonly string[]).includes(value)
    ? (value as StudioDefaultTab)
    : null;
}

export function readStoredStudioDefaultTab(): StudioDefaultTab | null {
  if (typeof window === "undefined") return null;
  try {
    return parseStudioDefaultTab(
      window.localStorage.getItem(STUDIO_DEFAULT_TAB_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

export function writeStoredStudioDefaultTab(tab: StudioDefaultTab) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STUDIO_DEFAULT_TAB_STORAGE_KEY, tab);
  } catch {
    /* ignore */
  }
}
